import { useCallback, useRef, useState } from 'react'
import { FileUp, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

interface DropzoneProps {
  /** Single-file mode: the preview flow, which reviews one document at a time. */
  onFile?: (file: File) => void
  /** Multi-file mode. When given, every dropped PDF is handed over at once. */
  onFiles?: (files: File[]) => void
  busy: boolean
  /** Overrides the idle caption; the bulk case says something different. */
  label?: string
  hint?: string
}

/**
 * Whole-surface drop target. A single drop starts the entire run — no metadata
 * form up front, because the point of a preview is to see the result first and
 * only then decide whether it is worth keeping.
 *
 * Accepts a batch when `onFiles` is given. The distinction matters: dropping
 * ten files on the preview zone cannot mean "preview all ten", so that case
 * still takes the first, but it now says so rather than silently discarding
 * the rest.
 */
export function Dropzone({ onFile, onFiles, busy, label, hint }: DropzoneProps) {
  const { t } = useTranslation()
  const [over, setOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const depth = useRef(0)
  const multiple = !!onFiles

  const take = useCallback(
    (files: FileList | null) => {
      const pdfs = Array.from(files ?? []).filter(
        (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'),
      )
      if (!pdfs.length) return
      if (onFiles) {
        onFiles(pdfs)
      } else if (onFile) {
        onFile(pdfs[0])
        // Dropping a batch on a one-at-a-time zone is a misunderstanding worth
        // naming — the old code just took [0] and said nothing.
        if (pdfs.length > 1) toast.info(t('dropzone.singleOnly', { count: pdfs.length }))
      }
    },
    [onFile, onFiles, t],
  )

  return (
    <div
      // dragenter/leave fire for every child element, so count depth instead of
      // toggling — otherwise the highlight flickers as the cursor crosses text.
      onDragEnter={(e) => {
        e.preventDefault()
        depth.current += 1
        setOver(true)
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        depth.current -= 1
        if (depth.current <= 0) setOver(false)
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        depth.current = 0
        setOver(false)
        if (!busy) take(e.dataTransfer.files)
      }}
      onClick={() => !busy && inputRef.current?.click()}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-10 transition-colors',
        over
          ? 'border-primary bg-accent'
          : 'border-border bg-card hover:border-ring hover:bg-muted',
        busy && 'pointer-events-none opacity-60',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          take(e.target.files)
          e.target.value = ''
        }}
      />
      {busy ? (
        <Loader2 className="size-8 animate-spin text-primary" />
      ) : (
        <FileUp className={cn('size-8', over ? 'text-primary' : 'text-muted-foreground')} />
      )}
      <div className="text-center">
        <p className="text-sm font-medium">
          {busy ? t('dropzone.busy') : (label ?? t('dropzone.idle'))}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">{hint ?? t('dropzone.hint')}</p>
      </div>
    </div>
  )
}
